import type { EstateItem } from "@/shared/types/estateTypes";

export function getVehicleLabel(item: EstateItem, fallback = "Vehicle"): string {
  const meta = item.metadata as Record<string, unknown> | undefined;
  return (
    [meta?.year, meta?.make, meta?.model].filter(Boolean).join(" ") || item.item_name || fallback
  );
}

export function getVehiclePrefix(selectedId: string): string {
  return selectedId.startsWith("new-")
    ? `new${parseInt(selectedId.replace("new-", ""), 10)}_`
    : `est${selectedId}_`;
}

export function splitMakeModel(makeModel: string): { make: string; model: string } {
  const parts = makeModel.split(/\s+/);
  return {
    make: parts[0] ?? "",
    model: parts.slice(1).join(" "),
  };
}

export function buildVehicleFieldMap(
  meta: Record<string, unknown>,
  defaults?: { userFullName?: string; relationship?: string },
): Record<string, string> {
  const make = String(meta.make ?? "");
  const model = String(meta.model ?? "");
  return {
    vehicle_make_model: [make, model].filter(Boolean).join(" "),
    vehicle_year: String(meta.year ?? ""),
    vehicle_VIN: String(meta.vin ?? ""),
    vehicle_body_type: String(meta.body_style ?? ""),
    license_plate_number: String(meta.plate_number ?? ""),
    vehicle_state: String(meta.state_issued ?? ""),
    transfer_recipient_name:
      String(meta.transfer_recipient_name ?? "") || defaults?.userFullName || "",
    relationship_to_decedent:
      String(meta.relationship_to_decedent ?? "") || defaults?.relationship || "",
    other_heirs: String(meta.other_heirs ?? ""),
  };
}

const VEHICLE_TRANSFER_KEYS = [
  "vehicle_VIN",
  "vehicle_body_type",
  "license_plate_number",
  "vehicle_state",
  "transfer_recipient_name",
  "relationship_to_decedent",
  "other_heirs",
] as const;

export function buildVehicleMetadataFromForm(
  formData: Record<string, string>,
  prefix: string,
  existing?: Record<string, unknown>,
): Record<string, unknown> {
  const { make, model } = splitMakeModel(formData[`${prefix}vehicle_make_model`] ?? "");
  return {
    ...(existing ?? {}),
    year: formData[`${prefix}vehicle_year`] || existing?.year,
    make: make || existing?.make,
    model: model || existing?.model,
    vin: formData[`${prefix}vehicle_VIN`] || existing?.vin,
    body_style: formData[`${prefix}vehicle_body_type`] || existing?.body_style,
    plate_number: formData[`${prefix}license_plate_number`] || existing?.plate_number,
    state_issued: formData[`${prefix}vehicle_state`] || existing?.state_issued,
    transfer_recipient_name: formData[`${prefix}transfer_recipient_name`] || undefined,
    relationship_to_decedent: formData[`${prefix}relationship_to_decedent`] || undefined,
    other_heirs: formData[`${prefix}other_heirs`] || undefined,
  };
}

export function resolveVehicleFormData(
  formData: Record<string, string>,
  prefix: string,
  meta: Record<string, unknown>,
  isNewVehicle: boolean,
): Record<string, string> {
  const resolved: Record<string, string> = {};
  for (const key of VEHICLE_TRANSFER_KEYS) {
    resolved[key] = formData[`${prefix}${key}`] ?? formData[key] ?? "";
  }
  if (!isNewVehicle) {
    const make = String(meta.make ?? "");
    const model = String(meta.model ?? "");
    resolved.vehicle_make_model = [make, model].filter(Boolean).join(" ");
    resolved.vehicle_year = String(meta.year ?? "");
    if (!resolved.vehicle_VIN) resolved.vehicle_VIN = String(meta.vin ?? "");
    if (!resolved.vehicle_body_type) resolved.vehicle_body_type = String(meta.body_style ?? "");
    if (!resolved.license_plate_number)
      resolved.license_plate_number = String(meta.plate_number ?? "");
    if (!resolved.vehicle_state) resolved.vehicle_state = String(meta.state_issued ?? "");
    if (!resolved.transfer_recipient_name)
      resolved.transfer_recipient_name = String(meta.transfer_recipient_name ?? "");
    if (!resolved.relationship_to_decedent)
      resolved.relationship_to_decedent = String(meta.relationship_to_decedent ?? "");
    if (!resolved.other_heirs) resolved.other_heirs = String(meta.other_heirs ?? "");
  } else {
    resolved.vehicle_make_model = formData[`${prefix}vehicle_make_model`] ?? "";
    resolved.vehicle_year = formData[`${prefix}vehicle_year`] ?? "";
  }
  return resolved;
}
